import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Global scroll manager for internal React Router navigation.
 *
 * Behaviour:
 *   • On every pathname or search change, scroll the window to (0, 0)
 *     with behavior: "auto" (no smooth scroll — that would produce a
 *     visible glide on every route change).
 *   • If the URL carries a valid, non-empty hash that resolves to a
 *     real element (either by matching `id` or by `name`), scroll to
 *     that element instead of forcing the top. This preserves normal
 *     anchor-link behaviour where it is intentional.
 *
 * Mount once inside <BrowserRouter>, above the <Routes> tree.
 * Renders nothing.
 */
export default function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      // Only treat the hash as intentional if it resolves to a real element.
      const el = id
        ? document.getElementById(id) ||
          document.querySelector(`[name="${CSS.escape(id)}"]`)
        : null;
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search, hash]);

  return null;
}
